import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from './AppContext';
import { normalizeCompanyContext } from '../utils/companyContract';
import { orderAgendaItems } from '../utils/agendaOrder';

const MeetingConfigContext = createContext(null);

export const useMeetingConfig = () => {
  const ctx = useContext(MeetingConfigContext);
  if (!ctx) throw new Error('useMeetingConfig must be used within MeetingConfigProvider');
  return ctx;
};

// ── Agenda presets by meeting type ──────────────────────────────────────────
export const AGENDA_PRESETS = {
  board: [
    { id: 'a_confirm_prev', label: 'Confirmation of Minutes of Previous Meeting', checked: true },
    { id: 'a_adopt_fin', label: 'Adoption of Audited Financial Statements', checked: true },
    { id: 'a_mgt7', label: 'Approval of Annual Return (MGT-7 / MGT-7A)', checked: true },
    { id: 'a_fix_agm', label: 'Fix Date / Time / Venue of Annual General Meeting', checked: true },
    { id: 'a_bank', label: 'Bank Account Resolution / Change of Signatories', checked: false },
    { id: 'a_appt_dir', label: 'Appointment / Change of Director', checked: false },
    { id: 'a_appt_auditor', label: 'Appointment / Ratification of Auditor', checked: false },
    { id: 'a_dividend', label: 'Declaration of Interim / Final Dividend', checked: false },
    { id: 'a_loan', label: 'Approval of Loan / OD / CC Facility', checked: false },
    { id: 'a_csr', label: 'CSR Committee / Policy Approval', checked: false },
    { id: 'a_rpt', label: 'Approval of Related Party Transaction (Sec 188)', checked: false },
    { id: 'a_aoc4', label: 'Authorise Filing of AOC-4', checked: false },
    { id: 'a_other', label: 'Any Other Business with Permission of Chair', checked: true },
  ],
  agm: [
    { id: 'a_adopt_accounts', label: 'Adoption of Audited Financial Statements', checked: true },
    { id: 'a_dividend', label: 'Declaration of Final Dividend (if any)', checked: true },
    { id: 'a_reappt_director', label: 'Re-appointment of Director retiring by rotation', checked: true },
    { id: 'a_appt_auditor', label: 'Appointment / Re-appointment of Statutory Auditor', checked: true },
    { id: 'a_special_business', label: 'Special Business (if any)', checked: false },
    { id: 'a_other', label: 'Any Other Business with Permission of Chair', checked: true },
  ],
  egm: [
    { id: 'a_special_res', label: 'Special Resolution (as per Notice)', checked: true },
    { id: 'a_confirm_notice', label: 'Confirm Service of Notice to all Members', checked: true },
    { id: 'a_other', label: 'Any Other Business with Permission of Chair', checked: true },
  ],
  committee: [
    { id: 'a_confirm_prev', label: 'Confirmation of Previous Meeting Minutes', checked: true },
    { id: 'a_review', label: 'Committee-Specific Review Items', checked: true },
    { id: 'a_other', label: 'Any Other Business', checked: true },
  ],
};

// ── Date / FY utilities (shared) ────────────────────────────────────────────
export function toInputDate(value) {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

export function formatDisplayDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

// Native <input type="time"> emits 24h "HH:MM". Convert to "hh:mm A.M./P.M."
// for the preview document and the AI payload. Pass-through anything that
// isn't HH:MM (already-formatted strings, empty, etc.).
export function formatDisplayTime(value) {
  if (!value) return '';
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value).trim());
  if (!m) return value;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const period = h >= 12 ? 'P.M.' : 'A.M.';
  h = h % 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, '0')}:${min} ${period}`;
}

// Reverse: "03:00 P.M." → "15:00" so legacy/cached drafts hydrate the
// native time picker correctly. Pass-through for already-24h strings.
export function parseDisplayTime(value) {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2}):(\d{2})\s*([AP])\.?\s*M\.?$/i.exec(s);
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const isPM = m[3].toUpperCase() === 'P';
  if (h === 12) h = isPM ? 12 : 0;
  else if (isPM) h += 12;
  return `${String(h).padStart(2, '0')}:${min}`;
}

function deriveFinancialYear(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = date.getMonth();
  if (m >= 3) return `${y}-${String(y + 1).slice(-2)}`;
  return `${y - 1}-${String(y).slice(-2)}`;
}

// ── Build the payload sent to aiAPI.draftMeetingMinutes ─────────────────────
export function buildMeetingPayload(config, companyContext, directorsPresent, absentDirectors, invitees, agendaItems) {
  return {
    meetingType: config.meetingType,
    meetingNumber: config.meetingNo === 'custom' ? (config.meetingNoCustom || '') : config.meetingNo,
    meetingDate: formatDisplayDate(config.date) || config.date,
    startTime: formatDisplayTime(config.startTime),
    endTime: formatDisplayTime(config.endTime),
    venue: config.venue || companyContext.address,
    modeOfMeeting: config.mode,
    noticeDate: config.noticeDate ? formatDisplayDate(config.noticeDate) : '',
    chairperson: config.chairperson,
    companySecretary: config.companySecretary,
    statutoryAuditor: config.statutoryAuditor || companyContext.auditor,
    financialYear: config.fy,
    previousMeetingDate: config.previousMeetingDate ? formatDisplayDate(config.previousMeetingDate) : '',
    previousMeetingNumber: config.previousMeetingNumber,
    proposedAgmDate: config.proposedAgmDate ? formatDisplayDate(config.proposedAgmDate) : '',
    proposedAgmVenue: config.proposedAgmVenue,
    dividendRecommendation: config.dividendRecommendation,
    directorsPresent,
    directorsAbsentWithLeave: absentDirectors,
    invitees,
    agendaItems: agendaItems.map((label, index) => ({
      number: index + 1,
      title: label,
      discussion: '',
      resolvedClauses: [],
      furtherResolvedClauses: [],
    })),
  };
}

// ── Session persistence ─────────────────────────────────────────────────────
const SESSION_KEY = 'cs_meeting_config';

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// meetingType/date/mode = sensible defaults; other fields start empty so
// placeholders show. fy auto-derives from date via the effect below.
const DEFAULT_CONFIG = {
  meetingType: 'board',
  meetingNo: '',
  meetingNoCustom: '',
  fy: '',
  date: toInputDate(new Date().toISOString()),
  startTime: '',
  endTime: '',
  mode: 'Physical — Registered Office',
  venue: '',
  noticeDate: '',
  chairperson: '',
  companySecretary: '',
  statutoryAuditor: '',
  previousMeetingDate: '',
  previousMeetingNumber: '',
  proposedAgmDate: '',
  proposedAgmVenue: '',
  dividendRecommendation: '',
};

// ─────────────────────────────────────────────────────────────────────────────
export const MeetingConfigProvider = ({ children }) => {
  const { activeCompany } = useApp();
  const companyContext = useMemo(() => normalizeCompanyContext(activeCompany || {}), [activeCompany]);

  // Read session snapshot once on mount
  const sessionSnapshot = useRef(readSession());
  const skipAgendaResetRef = useRef(!!sessionSnapshot.current);
  const skipDirectorResetRef = useRef(!!sessionSnapshot.current);

  // ── 7 core states ──────────────────────────────────────────────────────────
  const [config, setConfig] = useState(() => sessionSnapshot.current?.config || { ...DEFAULT_CONFIG });
  const [agendaChecked, setAgendaChecked] = useState(() => sessionSnapshot.current?.agendaChecked || {});
  const [customItems, setCustomItems] = useState(() => sessionSnapshot.current?.customItems || []);
  const [directorsChecked, setDirectorsChecked] = useState(() => sessionSnapshot.current?.directorsChecked || {});
  const [customDirectors, setCustomDirectors] = useState(() => sessionSnapshot.current?.customDirectors || []);
  const [absentDirectors, setAbsentDirectors] = useState(() => sessionSnapshot.current?.absentDirectors || []);
  const [invitees, setInvitees] = useState(() => sessionSnapshot.current?.invitees || []);

  // Company-switch signal — pages watch this to reset their own view-state
  const [companySwitchCount, setCompanySwitchCount] = useState(0);
  const prevCinRef = useRef(activeCompany?.cin || null);

  // ── Company sync effect ──────────────────────────────────────────────────
  useEffect(() => {
    if (!activeCompany) return;
    const isSwitch = prevCinRef.current && prevCinRef.current !== activeCompany.cin;
    prevCinRef.current = activeCompany.cin;

    setConfig((prev) => ({
      ...prev,
      // venue is no longer auto-stamped with the company's full registered
      // address — that string is too long to read as a form value. Preview
      // and AI gen still fall back to companyContext.address when venue is empty.
      venue: isSwitch ? '' : prev.venue,
      statutoryAuditor: isSwitch ? (companyContext.auditor || '') : (prev.statutoryAuditor || companyContext.auditor),
      chairperson: isSwitch ? (companyContext.directors[0]?.name || '') : (prev.chairperson || (companyContext.directors[0]?.name || '')),
    }));

    // Skip director reset if we just restored from session
    if (skipDirectorResetRef.current) {
      skipDirectorResetRef.current = false;
      if (!isSwitch) return; // first load with session data — keep restored directors
    }

    const initial = {};
    (companyContext.directors || []).forEach((d) => {
      initial[d.din || d.name] = true;
    });
    setDirectorsChecked(initial);
    setCustomDirectors([]);

    if (isSwitch) setCompanySwitchCount((c) => c + 1);
  }, [activeCompany, companyContext]);

  // ── MeetingType → agenda reset ────────────────────────────────────────────
  useEffect(() => {
    if (skipAgendaResetRef.current) {
      skipAgendaResetRef.current = false;
      return;
    }
    const presets = AGENDA_PRESETS[config.meetingType] || AGENDA_PRESETS.board;
    const initial = {};
    presets.forEach((p) => { initial[p.id] = p.checked; });
    setAgendaChecked(initial);
    setCustomItems([]);
  }, [config.meetingType]);

  // ── FY auto-derive ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!config.date) return;
    const derived = deriveFinancialYear(config.date);
    if (derived && derived !== config.fy) {
      setConfig((prev) => ({ ...prev, fy: derived }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.date]);

  // ── Persist to sessionStorage (debounced) ─────────────────────────────────
  const sessionTimerRef = useRef(null);
  useEffect(() => {
    clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({
          config, agendaChecked, customItems,
          directorsChecked, customDirectors, absentDirectors, invitees,
        }));
      } catch { /* quota — ignore */ }
    }, 150);
  }, [config, agendaChecked, customItems, directorsChecked, customDirectors, absentDirectors, invitees]);

  // ── Simple setter ─────────────────────────────────────────────────────────
  const updateConfig = useCallback((field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Bulk restore (from draft cache / History) ─────────────────────────────
  const restoreConfig = useCallback((mc) => {
    if (!mc) return;
    if (mc.config) {
      // Re-normalize legacy "03:00 P.M." times back to 24h "HH:MM" so the
      // native time picker hydrates properly.
      const next = { ...mc.config };
      if (next.startTime) next.startTime = parseDisplayTime(next.startTime) || next.startTime;
      if (next.endTime) next.endTime = parseDisplayTime(next.endTime) || next.endTime;
      setConfig((prev) => {
        // A restore that changes meetingType AND carries its own agenda state
        // must survive the meetingType→preset-reset effect that fires next —
        // same latch the session snapshot uses. (Set inside the updater so the
        // comparison sees the live meetingType; idempotent under StrictMode.)
        if (next.meetingType && next.meetingType !== prev.meetingType && (mc.agendaChecked || mc.customItems)) {
          skipAgendaResetRef.current = true;
        }
        return { ...prev, ...next };
      });
    }
    if (mc.agendaChecked) setAgendaChecked(mc.agendaChecked);
    if (mc.customItems) setCustomItems(mc.customItems);
    if (mc.directorsChecked) setDirectorsChecked(mc.directorsChecked);
    if (mc.customDirectors) setCustomDirectors(mc.customDirectors);
    if (mc.absentDirectors) setAbsentDirectors(mc.absentDirectors);
    if (mc.invitees) setInvitees(mc.invitees);
  }, []);

  // ── Reset to defaults ─────────────────────────────────────────────────────
  const resetToDefaults = useCallback(() => {
    setConfig({ ...DEFAULT_CONFIG, date: toInputDate(new Date().toISOString()) });
    setAgendaChecked({});
    setCustomItems([]);
    setDirectorsChecked({});
    setCustomDirectors([]);
    setAbsentDirectors([]);
    setInvitees([]);
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }, []);

  // ── Derived values ────────────────────────────────────────────────────────
  const getPresets = useCallback(
    () => AGENDA_PRESETS[config.meetingType] || AGENDA_PRESETS.board,
    [config.meetingType],
  );

  const selectedAgendaLabels = useMemo(() => {
    const presets = AGENDA_PRESETS[config.meetingType] || AGENDA_PRESETS.board;
    const all = [
      ...presets.map((p) => ({ id: p.id, label: p.label })),
      ...customItems.map((t, i) => ({ id: `custom_${i}`, label: t })),
    ];
    const selected = all.filter(
      (item) => item.id.startsWith('custom_') || agendaChecked[item.id],
    );
    // "Any Other Business" must always be the last agenda item — even when a custom
    // item was added after it. Numbering is positional downstream, so ordering the
    // labels here is the single fix (preview + AI payload both read this).
    return orderAgendaItems(selected).map((item) => item.label);
  }, [agendaChecked, customItems, config.meetingType]);

  const directorsPresent = useMemo(() => {
    const fromCompany = (companyContext.directors || [])
      .filter((d) => directorsChecked[d.din || d.name])
      .map((d) => ({
        name: d.name,
        designation: d.designation,
        din: d.din,
        mode: config.mode?.includes('Video') ? 'Video' : 'Present',
      }));
    const fromCustom = customDirectors.map((d) => {
      const dir = typeof d === 'string' ? { name: d } : (d || {});
      return {
        name: dir.name,
        designation: dir.designation || 'Director',
        din: dir.din || '',
        mode: config.mode?.includes('Video') ? 'Video' : 'Present',
      };
    });
    return [...fromCompany, ...fromCustom];
  }, [companyContext.directors, directorsChecked, customDirectors, config.mode]);

  const absentDirectorsNormalized = useMemo(
    () => absentDirectors.map((name) => ({ name, designation: 'Director', din: '' })),
    [absentDirectors],
  );

  const inviteesNormalized = useMemo(
    () => invitees.map((name) => ({ name, role: 'Invitee' })),
    [invitees],
  );

  // ── Build full AI payload ─────────────────────────────────────────────────
  const buildPayload = useCallback(
    () => buildMeetingPayload(config, companyContext, directorsPresent, absentDirectorsNormalized, inviteesNormalized, selectedAgendaLabels),
    [config, companyContext, directorsPresent, absentDirectorsNormalized, inviteesNormalized, selectedAgendaLabels],
  );

  // ── Provider value ────────────────────────────────────────────────────────
  const value = useMemo(() => ({
    // State
    config, agendaChecked, customItems,
    directorsChecked, customDirectors, absentDirectors, invitees,
    // Setters
    updateConfig, setConfig, setAgendaChecked, setCustomItems,
    setDirectorsChecked, setCustomDirectors, setAbsentDirectors, setInvitees,
    restoreConfig, resetToDefaults,
    // Derived
    directorsPresent, selectedAgendaLabels,
    absentDirectorsNormalized, inviteesNormalized,
    // Utilities
    buildPayload, getPresets, companyContext,
    // Signal
    companySwitchCount,
    AGENDA_PRESETS,
  }), [
    config, agendaChecked, customItems,
    directorsChecked, customDirectors, absentDirectors, invitees,
    updateConfig, restoreConfig, resetToDefaults,
    directorsPresent, selectedAgendaLabels,
    absentDirectorsNormalized, inviteesNormalized,
    buildPayload, getPresets, companyContext,
    companySwitchCount,
  ]);

  return (
    <MeetingConfigContext.Provider value={value}>
      {children}
    </MeetingConfigContext.Provider>
  );
};

export default MeetingConfigContext;
